import { createFocusTrap, FocusTrap } from 'focus-trap';
import { html, nothing, PropertyValueMap, TemplateResult, CSSResultGroup } from 'lit';
import { property, queryAssignedNodes, state } from 'lit/decorators.js';
import { query } from 'lit/decorators/query.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { AutoPositionController } from '../../controllers/autoPositionOverlay';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksCancelEvent } from '../../events';
import { BlocksCloseEvent } from '../../events/blocks-close';
import { BlocksOpenEvent } from '../../events/blocks-open';
import type { AutoPositionInterface, Position } from '../../util/common-modal';
import { MOBILE_BREAK_POINT } from '../../util/common-modal';
import { KEYS } from '../../util/event-keys';
import { isPopoverSupported } from '../../util/popover';
import { BlocksElement } from '../BlocksElement';
import '../button/button';
import { BlocksButton } from '../button/button';
import '../heading/heading';
import '../icon/icons/close';
import type { PopoverBehavior, PopoverHeadingTagName, PopoverHeadingVariant, PopoverPosition, PopoverSize } from './popover.model';
import styles from './popover.styles';

/**
 * @slot trigger - To trigger the component
 * @slot header - Content appearing above the default slot
 * @slot default - The default content
 * @slot footer - Content appearing below the default slot
 * @fires bl-popover-open - emitted when component is opened
 * @fires bl-popover-close - emitted when component is closed
 * @fires bl-popover-cancel - emitted before emitting close event so that user can prevent closing popover
 * @csspart base - The component's base wrapper.
 * @csspart part-trigger - The component's trigger slot.
 * @csspart panel-wrapper - The component's panel wrapper.
 * @csspart panel - The component's panel element.
 * @csspart header-wrap - The component's header wrapper.
 * @csspart header-content - The component's header content.
 * @csspart part-heading - The component's heading.
 * @csspart part-header - The component's header slot.
 * @csspart close-button - The component's close-button.
 * @csspart close-button-icon - The component's `<cat-icon-close>` icon.
 * @csspart body - The component's body.
 * @csspart part-default - The component's default slot.
 * @csspart footer-wrapper - The component's footer wrapper.
 * @csspart part-footer - The component's footer slot.
 */
export class BlocksPopover extends BlocksElement implements AutoPositionInterface {static styles: CSSResultGroup = styles;
  protected autoPositionController = new AutoPositionController(this);
  /**
   * Close button text
   */
  @property({ type: String })
  closeButtonText = 'Close';

  /**
   * The text that displays in the popover header.
   * 1) Will only display a max of two lines.
   */
  @property({ type: String })
  heading: string | undefined;

  /**
   * Popover heading variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**display-xl** renders a heading with the cat-theme-typography-preset-display-xl preset</li>
   * <li>**display-lg** renders a heading with the cat-theme-typography-preset-display-lg preset</li>
   * <li>**display** renders a heading with the cat-theme-typography-preset-display preset</li>
   * <li>**display-sm** renders a heading with the cat-theme-typography-preset-display-sm preset</li>
   * <li>**headline-lg** renders a heading with the cat-theme-typography-preset-headline-lg preset</li>
   * <li>**headline** renders a heading with the cat-theme-typography-preset-headline preset</li>
   * <li>**title** renders a heading with the cat-theme-typography-preset-title preset</li>
   * <li>**title-sm** renders a heading with the cat-theme-typography-preset-title-sm</li>
   * <li>**label-lg** renders a heading with the cat-theme-typography-preset-label-lg</li>
   * <li>**label** renders a heading with the cat-theme-typography-preset-label</li>
   * <li>**label-sm** renders a heading with the cat-theme-typography-preset-label-sm</li>
   * <li>**body** renders a heading with the cat-theme-typography-preset-body preset</li>
   * <li>**body-sm** renders a heading with the cat-theme-typography-preset-body-sm preset</li>
   * <li>**footnote** renders a heading with the cat-theme-typography-preset-footnote preset</li>
   * <li>**footnote-bold** renders a heading with the cat-theme-typography-preset-footnote-bold preset</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  headingVariant: PopoverHeadingVariant = 'title';

  /**
   * Popover heading tagname
   * 1) This is needed to use proper semantic heading treatments depending on where it lives on the page
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**h1** renders an `h1` tag</li>
   * <li>**h2** renders an `h2` tag. This is the default</li>
   * <li>**h3** renders an `h3` tag</li>
   * <li>**h4** renders an `h4` tag</li>
   * <li>**h5** renders an `h5` tag</li>
   * <li>**h6** renders an `h6` tag</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  headingTagName: PopoverHeadingTagName = 'h3';

  /**
   * Positions the dropdown panel absolutely to the button.
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**default** places the popover panel to the bottom left</li>
   * <li>**bottom** places the popover panel to the bottom center</li>
   * <li>**bottom-end** places the popover panel to the bottom right</li>
   * <li>**bottom-start** places the popover panel to the bottom left</li>
   * <li>**top** places the popover panel to the top center</li>
   * <li>**top-end** places the popover panel to the top right</li>
   * <li>**top-start** places the popover panel to the top left</li>
   * <li>**left** places the popover panel to the left</li>
   * <li>**right** places the popover panel to the right</li>
   * </ul>
   * If `isInline` is false, and if specified position doesn't fit inside the viewport,
   * it will try to accommodate in other positions which fits best.
   * </cat-text-passage>
   */
  @property({ type: String })
  position: PopoverPosition = 'auto';

  /**
   * Adjusts the max width of the popover panel.
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**lg** Adds a max width of 50rems to the popover panel</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  size: PopoverSize;

  /**
   * Adjusts the size of the popover panel.
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**responsive** Makes the popover panel full screen on small screen</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  behavior: PopoverBehavior;

  /**
   * Popover variant
   */
  @property({ type: Boolean })
  noScroll: boolean | undefined;

  /**
   * Aria labelled by attribute
   */
  @property({ type: String })
  ariaLabelledBy: string | undefined;

  /**
   *  Append an active state on the button
   */
  @property({ type: Boolean })
  isActive: boolean | undefined;

  /**
   * Determines the behavior of the panel based on the value of the `isInline` property.
   * If `isInline` is false and the popover attribute is supported by the browser,
   * the panel will be opened inside the top layer.
   * If none of the position fits and if it goes out of viewport, scroll will be enabled if `isInline` is false.
   * If `isInline` is true, the popover attribute will not be used, and the panel
   * will use the floating UI absolute strategy.
   *  @property {Boolean} isInline - Default is false.
   */
  @property({ type: Boolean })
  isInline = false;

  /**
   * **Breakpoint below which popover switches to mobile view.Defaults to 768px.**
   */
  @property({ type: Number })
  mobileBreakPoint = MOBILE_BREAK_POINT;

  /**
   * Dynamic active state
   */
  @state()
  private isActiveDynamic: boolean | undefined;

  private _focusTrap?: FocusTrap;

  /**
   * Query the popover panel element
   */

  @query('.cat-c-popover__panel-wrapper')
  private _catPopoverPanel!: HTMLElement;

  @query('.cat-c-popover__panel')
  private _catPopoverPanelChild!: HTMLElement;

  @query('.cat-c-popover__close-button')
  private _catPopoverCloseButton: BlocksButton | undefined;

  @queryAssignedNodes({ slot: 'trigger', flatten: true }) protected triggeredslot!: Array<BlocksElement>;

  private isPopOverSupported = isPopoverSupported();

  private wasActive = false;

  protected slots = new SlotController(this, 'trigger', 'header', null, 'footer');

  /**
   * Retrieves the class names for the popover component when displayed on small screens.
   * @returns {string} The class names for the popover component, including responsive and full-screen classes if applicable.
   */
  public get getSmallScreenClassNames(): string {
    return this.isSmallScreen
      ? `cat-c-popover__panel--full-screen ${this.behavior === 'responsive' ? 'cat-c-popover__panel--responsive' : ''}`.trim()
      : '';
  }

  private get isPopoverEnabled(): boolean {
    return this.isPopOverSupported && !this.isInline;
  }

  public getFloatingElement(): HTMLElement {
    return this._catPopoverPanel;
  }
  public getPosition(): Position {
    return this.position as Position;
  }
  public getIsInline(): boolean {
    return this.isInline;
  }
  public getReferenceElement(): HTMLElement {
    const catPopoverTrigger = this.triggeredslot[0];
    return catPopoverTrigger;
  }
  public getOverflowElement(): HTMLElement | null {
    return this._catPopoverPanelChild;
  }
  private get isSmallScreen(): boolean {
    return window.innerWidth < this.mobileBreakPoint;
  }

  /**
   * Connected callback lifecycle
   * 1) Add window resize event listener
   * 3) Add window orientation change event listener
   * 4) Add mousedown event listener
   * 5) Set the id and ariaLabelledBy
   * 6) Pausing the focustrap when dropdown open for enabling the arrow keys and unpausing the focustrap again once the dropdown close. Otherwise the dropdown in popover will not be accessable via arrow kays
   */
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.removeActive); /* 1 */
    window.addEventListener('orientationchange', this.removeActive); /* 3 */
    document.addEventListener('mousedown', this.handleOnClickOutside, false); /* 4 */
    this.ariaLabelledBy = this.ariaLabelledBy || nanoid(); /* 5 */
    this.removeActive();
    this.addEventListener('bl-dropdown-open', this.pauseFocusTrap, true); /* 6 */
    this.addEventListener('bl-dropdown-close', this.unPauseFocusTrap, true); /* 6 */
  }

  /**
   * Disconnected callback lifecycle
   * 1) Remove window resize event listener
   * 3) Remove window orientation change event listener
   * 4) Remove mousedown event listener
   * 5) Remove the dropdown panel open and close event listener
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.removeActive); /* 1 */
    window.removeEventListener('orientationchange', this.removeActive); /* 3 */
    document.removeEventListener('mousedown', this.handleOnClickOutside, false); /* 1 */
    this._focusTrap?.deactivate(); /**To deactivate focus trap */
    /* 5 */
    this.removeEventListener('bl-dropdown-open', this.pauseFocusTrap, true);
    this.removeEventListener('bl-dropdown-close', this.unPauseFocusTrap, true);
  }

  /**
   *  Pause the focustrap
   */
  private pauseFocusTrap() {
    this._focusTrap?.pause();
  }

  /**
   *  Unpause the focustrap
   */
  private unPauseFocusTrap() {
    this._focusTrap?.unpause();
  }

  updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('isActive')) {
      this.wasActive = _changedProperties.get('isActive') === undefined ? false : true;
      this.activatePopover();
    }
  }

  /**
   * Handle all dynamic placement
   */
  dynamicPosition() {
    const catPopoverTrigger = this.triggeredslot[0];
    if (this._catPopoverPanel && catPopoverTrigger) {
      if (!this.isSmallScreen) {
        this.autoPositionController.openOverlay();
      } else {
        this._catPopoverPanel.removeAttribute('style');
      }
      this._catPopoverPanel.style.display = 'block';
      if (this.isPopoverEnabled) {
        this._catPopoverPanel.showPopover();
      }
    }
  }

  /**
   * Hide calender popup
   */
  private hidePopup() {
    if (this.isPopoverEnabled) {
      this._catPopoverPanel.hidePopover();
    }
    this.autoPositionController.closeOverlay();
  }
  /**
   * Handle click outside the component
   * 1) Close the show hide panel on click outside
   * 2) If the nav is already closed then we don't care about outside clicks and we
   * can bail early
   * 3) By the time a user clicks on the page the shadowRoot will almost certainly be
   * defined, but TypeScript isn't that trusting and sees this.shadowRoot as possibly
   * undefined. To work around that we'll check that we have a shadowRoot (and a
   * rendered .host) element here to appease the TypeScript compiler. This should never
   * actually be shown or run for a human end user.
   * 4) Check to see if we clicked inside the active panel
   * 5) If the panel is active and we've clicked outside of the panel then it should
   * be closed.
   * 6) condition added to not include scroll as backdropclick, particularly when hidescroll is kept as false
   */
  private handleOnClickOutside = (event: MouseEvent) => {
    /* 2 */
    if (!this.isActive) {
      return;
    }
    /* 3 */
    if (!this.shadowRoot?.host) {
      throw Error('Could not determine panel context during click handler');
    }
    /* 4 */
    const didClickInside = event.composedPath().includes(this.shadowRoot.host);

    /* 5 */
    if (!(event.target === document.querySelector('html') && event.clientX >= document.documentElement.offsetWidth)) {
      if (this.isActive && !didClickInside) {
        this.toggle();
      }
    }
  };

  /**
   * Set Auto Focus
   * 1) Set focus to element which has autoFocus to true.
   * 2) If no autoFocus enabled, set focus to Popover close button.
   */
  private setAutoFocus() {
    const autoFocusTarget = this.querySelector('[autoFocus]');
    /* 1 */
    if (autoFocusTarget) {
      (autoFocusTarget as HTMLElement).focus();
    } else if (this._catPopoverCloseButton) {
      /* 2 */
      this._catPopoverCloseButton.focus();
    }
  }

  /**
   * Activate Popover
   * 1) Set attribute since aria expanded can't be passed down through the slot
   * 2) If the active state is false, focus on the dropdown trigger.
   * This accounts for both design system buttons and native HTML buttons
   */
  private activatePopover() {
    if (this.isActive) {
      setTimeout(() => {
        this.dynamicPosition();
        this.setAutoFocus();
        this.isActiveDynamic = true;
      }, 1);

      this.activateFocusTrap();
    } else {
      /* To deactivate focus trap*/
      this._focusTrap?.deactivate();
      this.hidePopup();
      /* 2 */
      setTimeout(() => {
        if (this.wasActive) {
          if (this.querySelector<HTMLElement>('cat-button')) {
            this.querySelector<HTMLElement>('cat-button')?.shadowRoot?.querySelector<HTMLButtonElement>('.cat-c-button')?.focus();
          } else if (this.querySelector<HTMLButtonElement>('button')) {
            this.querySelector<HTMLButtonElement>('button')?.focus();
          }
        }
        this.isActiveDynamic = false;
      }, 100);
    }
  }

  private activateFocusTrap() {
    setTimeout(() => {
      /** calling function to create focus trap */
      this._focusTrap = createFocusTrap(this._catPopoverPanel, {
        onActivate: () => this._catPopoverPanel.classList.add('is-active'),
        onDeactivate: () => this._catPopoverPanel.classList.remove('is-active'),
        initialFocus: false,
        tabbableOptions: { getShadowRoot: true },
        allowOutsideClick: true,
        fallbackFocus: this._catPopoverPanel //fallback element to programmatically receive focus if no other tabbable elements are found
      });
      /**To activate focus trap */
      this._focusTrap.activate();
    }, 100);
  }

  /**
   * Remove active state
   * 1) If a specific event is fired, remove the active state.
   * Prevent resize on mobile devices in order to prevent autoclose of popover when interacting with form elements that
   * trigger the virtual keyboard and fire a resize event.
   * Existing issue - https://issues.chromium.org/issues/41176235
   */
  private removeActive = () => {
    if (this.isActive && !this.isSmallScreen) {
      this.toggle();
    }
  };

  /**
   * Handle on keydown
   * 1) If the panel is open and escape is keyed, close the menu and return focus to the trigger button
   */
  private handleKeyDown(e: KeyboardEvent) {
    if (this.isActive === true && e.key === KEYS.ESCAPE) {
      this.toggle();
    }
  }

  /**
   * 1.Cancel event to allow the user not to close the popover.
   * 2.Toggle the active state between true and false and fire event
   */
  public toggle() {
    if (this.isActive && this.emitCancelEvent()) {
      /** 1 **/
      return;
    }
    /** 2 **/
    this.isActive = !this.isActive;

    if (this.isActive) {
      this.dispatch({ eventName: 'bl-popover-open', detailObj: { isActive: true } }) as BlocksOpenEvent;
    } else {
      this.dispatch({ eventName: 'bl-popover-close', detailObj: { isActive: this.isActive } }) as BlocksCloseEvent;
      setTimeout(() => {
        this.triggeredslot[0].focus();
      }, 1);
    }
  }

  /**
   * @fires cancel If we call preventDefault on cancel event object to not close popover.
   * @returns boolean
   */
  private emitCancelEvent(): boolean {
    const cancelEvent = this.dispatch({
      eventName: 'bl-popover-cancel',
      detailObj: {},
      optionsObj: { cancelable: true }
    }) as BlocksCancelEvent;

    return cancelEvent.defaultPrevented;
  }

  /**
   * Close Popover
   */
  public close() {
    this.isActive = false;
  }

  /**
   * Open Popover
   */
  public open() {
    this.isActive = true;
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-popover', {
      'cat-c-popover--lg': this.size === 'lg',
      'cat-c-popover--responsive': this.behavior === 'responsive',
      'cat-c-popover--no-scroll': this.noScroll === true,
      'cat-is-active-dynamic': this.isActiveDynamic === true,
      'cat-is-active': this.isActive === true,
      'cat-c-popover--has-trigger': this.slots.hasSlotted('trigger'),
      'cat-c-popover--has-footer': this.slots.hasSlotted('footer'),
      'cat-c-popover--has-header': !this.heading && this.slots.hasSlotted('header')
    });

    return html`
      <div part="base" class="${componentClassNames}" @keydown=${this.handleKeyDown}>
        <slot part="part-trigger" name="trigger" @click=${this.toggle}></slot>
        <div part="panel-wrapper" class="cat-c-popover__panel-wrapper" popover=${ifDefined(this.isPopoverEnabled ? 'manual' : undefined)}>
          ${this.isActive
            ? html`
                <div
                  part="panel"
                  class="cat-c-popover__panel ${this.getSmallScreenClassNames}"
                  role="dialog"
                  aria-labelledby=${ifDefined(this.ariaLabelledBy)}
                >
                  <div part="header-wrap" class="cat-c-popover__header">
                    <div part="header-content" class="cat-c-popover__header-content" id=${ifDefined(this.ariaLabelledBy)}>
                      ${this.heading
                        ? html` <cat-heading
                            part="part-heading"
                            exportparts=${this.getExportParts('heading')}
                            class="cat-c-popover__heading"
                            level=${ifDefined(this.headingTagName)}
                            variant=${ifDefined(this.headingVariant)}
                          >
                            ${this.heading}
                          </cat-heading>`
                        : nothing}
                      <slot part="part-header" name="header"></slot>
                    </div>
                    <cat-button
                      part="close-button"
                      exportparts=${this.getExportParts('button')}
                      class="cat-c-popover__close-button"
                      variant="ghost"
                      ?hideText=${true}
                      @click=${this.toggle}
                    >
                      ${this.closeButtonText}
                      <cat-icon-close part="close-button-icon" exportparts=${this.getExportParts('close', 'icon')} slot="after"></cat-icon-close>
                    </cat-button>
                  </div>
                  <div part="body" class="cat-c-popover__body">
                    <slot part="part-default"></slot>
                  </div>
                  <div part="footer-wrapper" class="cat-c-popover__footer"><slot part="part-footer" name="footer"></slot></div>
                </div>
              `
            : html`${nothing}`}
        </div>
      </div>
    `;
  }
}

if (customElements.get('cat-popover') === undefined) {
  customElements.define('cat-popover', BlocksPopover);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-popover': BlocksPopover;
  }
}
